import {
  detectStandaloneDisplay,
  readInstalledDisplayMode,
} from '@diamond/sol-buyer-context';

export { detectStandaloneDisplay, readInstalledDisplayMode };

export type WebPushCapability =
  | 'unsupported'
  | 'ios_safari_tab'
  | 'denied'
  | 'default'
  | 'granted';

export type WebPushCapabilityInput = {
  isIos: boolean;
  isStandalone: boolean;
  notificationPermission: NotificationPermission | null;
};

export type WebPushGateInput = WebPushCapabilityInput & {
  isAuthenticated: boolean;
  isServiceWorkerReady: boolean;
  isWebPushEnabled: boolean;
};

export function detectIosDevice({
  maxTouchPoints,
  platform,
  userAgent,
}: {
  maxTouchPoints: number;
  platform: string;
  userAgent: string;
}): boolean {
  if (/iPad|iPhone|iPod/.test(userAgent)) return true;
  return platform === 'MacIntel' && maxTouchPoints > 1;
}

export function getWebPushCapability({
  isIos,
  isStandalone,
  notificationPermission,
}: WebPushCapabilityInput): WebPushCapability {
  if (notificationPermission === null) return 'unsupported';
  if (isIos && !isStandalone) return 'ios_safari_tab';

  switch (notificationPermission) {
    case 'denied':
      return 'denied';
    case 'granted':
      return 'granted';
    case 'default':
      return 'default';
    default: {
      const exhaustive: never = notificationPermission;
      return exhaustive;
    }
  }
}

function isWebPushGatedOn(input: WebPushGateInput): boolean {
  return (
    input.isAuthenticated &&
    input.isWebPushEnabled &&
    input.isServiceWorkerReady
  );
}

export function canPromptWebPush(input: WebPushGateInput): boolean {
  return isWebPushGatedOn(input) && getWebPushCapability(input) === 'default';
}

export function canOfferWebPushRecovery(input: WebPushGateInput): boolean {
  return isWebPushGatedOn(input) && getWebPushCapability(input) === 'denied';
}

export function canRegisterWebPushToken(input: WebPushGateInput): boolean {
  return isWebPushGatedOn(input) && getWebPushCapability(input) === 'granted';
}
